// 組織名簿（ロスター）連携ヘルパー
// ------------------------------------------------------------------
// 「組織情報管理」Ph.3（名簿連携）で追加。CSVUserUploadPage / CSVUserUpdatePage /
// OrgMgrPage が共用する純関数群。CSV の所属列を組織マスターと突き合わせるための整形・
// エラーリスト CSV 生成・組織リスト（フルパス）抽出・名簿テンプレート Excel 生成を担う。
//
// - 所属の表現は 2 形式をサポート:
//   ① 個別列形式: department, department2 … department7（後方互換）
//   ② フルパス 1 列形式: 「所属フルパス」に '／' 連結（① より優先）
// - 系列会社ありグループ（「グループ会社・系列会社構造」PJ。options.affiliate=true）では
//   組織表の最上位が系列会社（level 0）になり、所属フルパスは「会社名／所属①／…」形式になる。
//   affiliate を渡さない呼び出しはすべて従来どおり（先頭セグメントも所属として扱う）。
import ExcelJS from 'exceljs';

// フルパス列のヘッダー名と階層区切り文字
// 2026-10-07: 他の列と同じ英字フィールド名 `department_path` に統一（旧ヘッダー「所属フルパス」も読み取り互換で受け付ける。
// 名簿テンプレート Excel の 4 行目キーも `department_path`。日本語ラベル（3 行目）は「所属（フルパス・選択式）」のまま）
export const FULL_PATH_HEADER = 'department_path';
export const LEGACY_FULL_PATH_HEADER = '所属フルパス';
export const PATH_SEP = '／';

// レコードのフルパス値を取得（新ヘッダー `department_path` 優先・無ければ旧ヘッダー「所属フルパス」）
export const getFullPathValue = (record) => {
  if (!record) return undefined;
  const v = record[FULL_PATH_HEADER];
  if (v !== undefined && v !== null && String(v).trim() !== '') return v;
  return record[LEGACY_FULL_PATH_HEADER];
};

// 所属の階層キー（department〜department7 = 最大 7 階層）
export const DEPT_KEYS = [
  'department',
  'department2',
  'department3',
  'department4',
  'department5',
  'department6',
  'department7',
];

// 文字列化して前後空白を除去する（undefined / null は空文字）
const trimText = (value) =>
  value === undefined || value === null ? '' : String(value).trim();

// 所属フルパスを '／' で分解（前後空白除去・空セグメント除去）
const splitFullPath = (full) =>
  String(full)
    .split(PATH_SEP)
    .map((s) => s.trim())
    .filter((s) => s !== '');

// 所属フルパスと会社名列が食い違う行のエラー文言（サーバー側の会社名エラーと同じく会社名エラーとして扱う）
export const COMPANY_PATH_CONFLICT_ERROR = '会社名と所属フルパスの会社名が一致しません';

/**
 * CSV の 1 レコードから department〜department7 を解決して返す。
 * 「所属フルパス」列があれば '／' で分解して割り当て（個別列より優先）、
 * 無ければ既存の個別列（department〜department7）をそのまま返す（後方互換）。
 *
 * options.affiliate が true（系列会社ありグループ）で「所属フルパス」列に値がある場合は、
 * フルパスを「会社名／所属①／…」形式（組織リストの値）とみなし、先頭セグメントを会社名、
 * 残りを department〜department7 に割り当てる。このとき戻り値に company を含める
 * （「会社名」列に値があればその値を優先し、空ならフルパスの会社名を使う。
 *   両者の食い違いは getCompanyPathConflict / buildCompanyPathConflictRows で検出する）。
 * @param {Object} record
 * @param {{ affiliate?: boolean }} [options]
 * @returns {Object} { department, department2, … department7, company? }（未定義階層は undefined）
 */
export const expandDepartments = (record, options = {}) => {
  const full = getFullPathValue(record);
  const out = {};
  if (typeof full === 'string' && full.trim() !== '') {
    let parts = splitFullPath(full);
    if (options.affiliate === true && parts.length > 0) {
      const pathCompany = parts[0];
      parts = parts.slice(1);
      const columnCompany = record ? record.company : undefined;
      out.company = trimText(columnCompany) !== '' ? columnCompany : pathCompany;
    }
    DEPT_KEYS.forEach((k, i) => {
      out[k] = parts[i] !== undefined ? parts[i] : undefined;
    });
  } else {
    DEPT_KEYS.forEach((k) => {
      out[k] = record ? record[k] : undefined;
    });
  }
  return out;
};

// レコードに「所属フルパス」列が有効値で入っているか
export const hasFullPath = (record) => {
  const full = getFullPathValue(record);
  return typeof full === 'string' && full.trim() !== '';
};

/**
 * フルパス列がある場合のみ、department〜department7 を上書きした
 * 正規化レコードを返す（無ければ元レコードをそのまま返す＝参照同一）。
 * 既存の department* 参照ロジックを壊さず 2 形式を吸収するための薄い層。
 * options.affiliate が true なら company もフルパスの会社名で補完する（expandDepartments 参照）。
 */
export const normalizeRecord = (record, options = {}) =>
  hasFullPath(record) ? { ...record, ...expandDepartments(record, options) } : record;

/**
 * 系列会社ありグループで「会社名」列と「所属フルパス」先頭の会社名が食い違うか。
 * 「会社名」列が空・フルパスが空・affiliate でない場合は false。
 * @param {Object} record
 * @param {{ affiliate?: boolean }} [options]
 * @returns {boolean}
 */
export const getCompanyPathConflict = (record, options = {}) => {
  if (options.affiliate !== true || !hasFullPath(record)) return false;
  const columnCompany = trimText(record.company);
  if (columnCompany === '') return false;
  const parts = splitFullPath(getFullPathValue(record));
  return parts.length > 0 && parts[0] !== columnCompany;
};

/**
 * 「会社名」列と「所属フルパス」の会社名が食い違う行を、会社名エラー行として返す。
 * 形は companyRule.validateCompanyRows と同じ（row は 1 起点）。conflict:true で識別する
 * （サーバー由来の会社名エラーと同じ行にあっても、この文言は残す＝mergeRowErrors 参照）。
 * @param {Object[]} records 名簿の行（fileJSON）
 * @param {{ affiliate?: boolean }} [options]
 * @returns {Array<{ row:number, ok:false, error:string, suggestions:[], field:'company', conflict:true }>}
 */
export const buildCompanyPathConflictRows = (records, options = {}) => {
  if (options.affiliate !== true || !Array.isArray(records)) return [];
  const out = [];
  records.forEach((record, index) => {
    if (getCompanyPathConflict(record, options)) {
      out.push({
        row: index + 1,
        ok: false,
        error: COMPANY_PATH_CONFLICT_ERROR,
        suggestions: [],
        field: 'company',
        conflict: true,
      });
    }
  });
  return out;
};

// レコードに所属情報（「所属フルパス」列 or department〜department7 のいずれか）が含まれているか
export const hasDepartmentInput = (record) =>
  !!record &&
  (hasFullPath(record) || DEPT_KEYS.some((k) => record[k] !== undefined));

/**
 * 所属 7 階層を「1 つの単位」として扱うための補完（2026-09）。
 * ------------------------------------------------------------------
 * 所属情報が 1 つでもある行は、記載の無い階層を '' で埋めて department〜department7 を
 * 全て確定させる。これにより送信側の `!== undefined` ゲートとサーバーの `$set` を必ず通り、
 * 「旧: 1〜4 階層 → 新: 1〜3 階層」の更新で旧 4 階層目がユーザー本体に残留しなくなる。
 * - 空値は単件編集（UserFormModal.resolveDeptFields）と同じ '' に統一（unset / null は使わない。
 *   undefined は JSON 送信時にキーごと脱落し「据え置き」になってしまう）
 * - 所属列が全く無い行は変更しない（所属は据え置き）
 * - ※ CSVUserUpdateOnlyPage（ユーザー本体の部分修正専用）には意図的に適用しない
 * @returns {Object} 補完済みレコード（所属情報が無ければ元レコードをそのまま返す）
 */
export const completeDepartmentChain = (record) => {
  if (!hasDepartmentInput(record)) return record;
  const out = { ...record };
  DEPT_KEYS.forEach((k) => {
    const v = out[k];
    out[k] = v === undefined || v === null ? '' : v;
  });
  return out;
};

/**
 * レコードの所属から企業マスター（GSD = Group.department）へ登録する経路プレフィックス配列を返す（2026-09）。
 * 例: 本社／営業部／一課 → ['本社', '本社／営業部', '本社／営業部／一課']
 * - 最初の空欄階層で切断（従来の入れ子 if と同じ規則）。値は従来どおり原文のまま連結する
 *   （User 本体の保存値と一致させ、集計フィルタの完全一致比較を崩さないため）
 * - 7 階層まで（従来は CSV 4 ページの入れ子 if が department5 で止まり、6・7 階層の経路が GSD に登録されなかった）
 * @returns {string[]} 経路プレフィックス（所属が無ければ空配列）
 */
export const buildDepartmentPrefixes = (record) => {
  const out = [];
  const acc = [];
  for (let i = 0; i < DEPT_KEYS.length; i++) {
    const v = record ? record[DEPT_KEYS[i]] : '';
    if (!v) break;
    acc.push(v);
    out.push(acc.join(PATH_SEP));
  }
  return out;
};

// ─── 所属名の入力規則（2026-09） ───
// 1 階層分の所属名に対する制限。サーバー `utils/departmentName.js` と同一規則（変更時は両方を同期すること）。
export const DEPT_NAME_MAX_LENGTH = 50;
// 制御文字（改行・タブ等）
const CONTROL_CHAR_RE = /[\u0000-\u001F\u007F]/;

/**
 * 所属名（1 階層分）を検証する。
 * - '／'（全角スラッシュ）は階層の区切り文字のため禁止
 * - 改行・タブ等の制御文字は禁止
 * - 前後空白を除いて 50 文字以内
 * 空（未設定）はここでは判定しない（必須かどうかは呼び出し側の責務）。
 * @returns {string} エラーメッセージ（問題なければ ''）
 */
export const validateDepartmentName = (name) => {
  const s = name === undefined || name === null ? '' : String(name).trim();
  if (s === '') return '';
  if (s.includes(PATH_SEP)) {
    return `所属名に「${PATH_SEP}」（全角スラッシュ）は使用できません（階層の区切り文字のため）`;
  }
  if (CONTROL_CHAR_RE.test(s)) {
    return '所属名に改行・タブなどの制御文字は使用できません';
  }
  if (s.length > DEPT_NAME_MAX_LENGTH) {
    return `所属名は ${DEPT_NAME_MAX_LENGTH} 文字以内で入力してください`;
  }
  return '';
};

/**
 * レコードの department〜department7 を検証し、最初のエラーを「所属N: 理由」で返す（無ければ ''）。
 * CSV / Excel アップロードの行検証用。
 */
export const validateRecordDepartments = (record) => {
  for (let i = 0; i < DEPT_KEYS.length; i++) {
    const err = validateDepartmentName(record ? record[DEPT_KEYS[i]] : '');
    if (err) return `所属${i + 1}: ${err}`;
  }
  return '';
};

/**
 * validate-roster 送信用の行配列を生成。
 * row は 1 起点（= ヘッダーを除いたデータ行番号）で、検証結果の row と対応する。
 * options.affiliate が true（系列会社ありグループ）のときだけ各行に company（前後空白除去）を含める
 * （「所属フルパス」が「会社名／…」形式なら会社名列が空でもフルパスの会社名を送る）。
 * それ以外は従来どおり company キーを付けない。
 * @param {Object[]} fileJSON
 * @param {{ affiliate?: boolean }} [options]
 * @returns {Array<{row:number, company?:string, d1:string … d7:string}>}
 */
export const buildRosterRows = (fileJSON, options = {}) => {
  const affiliate = options.affiliate === true;
  return (fileJSON || []).map((record, idx) => {
    const dept = expandDepartments(record, { affiliate });
    const row = { row: idx + 1 };
    if (affiliate) {
      row.company = trimText(
        dept.company !== undefined ? dept.company : record ? record.company : ''
      );
    }
    DEPT_KEYS.forEach((k, i) => {
      row[`d${i + 1}`] = dept[k] || '';
    });
    return row;
  });
};

/**
 * 組織ツリーの active ノードから antd `<Cascader>` 用の options を組み立てる。
 * ------------------------------------------------------------------
 * 「ユーザー単件編集」モーダル（UserFormModal）の所属選択で使用する。
 * ノードの識別は C′ 設計に合わせ lineage_id / parent_lineage_id（文字列比較）で辿り、
 * Cascader の value・label はどちらもノード名（= DB の department〜department7 に
 * 保存するテキスト）とする。並び順は organization 側の sort_order に従う。
 *
 * options.company を指定し、組織表に系列会社（level 0）ノードがある場合は、
 * その系列会社ノード（name 一致・active）の子孫だけを返す（系列会社ノード自体は含めない）。
 * 該当する系列会社が無ければ空配列。組織表に level 0 ノードが無ければ company は無視（従来どおり）。
 *
 * @param {Array} units GET /api/org/tree/:groupCode の units
 * @param {{ company?: string }} [options]
 * @returns {Array<{value:string, label:string, children?:Array}>}
 */
export const buildOrgCascaderOptions = (units, options = {}) => {
  const actives = (units || []).filter((u) => u && u.status === 'active');
  // parent_lineage_id をキーに子ノードをまとめる（ルートは親なし = '')
  const childrenOf = new Map();
  actives.forEach((u) => {
    const parent = u.parent_lineage_id ? String(u.parent_lineage_id) : '';
    if (!childrenOf.has(parent)) childrenOf.set(parent, []);
    childrenOf.get(parent).push(u);
  });
  // 親が active でないノード（親が停止中など）はルート扱いにせず除外する
  const activeIds = new Set(actives.map((u) => String(u.lineage_id)));
  const build = (parentKey, guard) => {
    const list = (childrenOf.get(parentKey) || [])
      .slice()
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    return list
      .filter((u) => !guard.has(String(u.lineage_id)))
      .map((u) => {
        const id = String(u.lineage_id);
        const nextGuard = new Set(guard);
        nextGuard.add(id);
        const children = build(id, nextGuard);
        const option = { value: u.name, label: u.name };
        if (children.length > 0) option.children = children;
        return option;
      });
  };
  // 系列会社指定: 該当する系列会社ノード（active・level 0）の子孫だけ
  const companyName =
    options.company === undefined || options.company === null
      ? null
      : trimText(options.company);
  const hasCompanyNodes = (units || []).some((u) => u && u.level === 0);
  if (companyName !== null && hasCompanyNodes) {
    const companyNode = actives.find(
      (u) => u.level === 0 && trimText(u.name) === companyName
    );
    if (!companyNode) return [];
    const id = String(companyNode.lineage_id);
    return build(id, new Set([id]));
  }
  // ルート = 親 lineage_id が無い、または親が active に存在しないノード
  const rootKeys = Array.from(childrenOf.keys()).filter(
    (key) => key === '' || !activeIds.has(key)
  );
  const roots = [];
  rootKeys.forEach((key) => {
    build(key, new Set()).forEach((opt) => roots.push(opt));
  });
  return roots;
};

/**
 * 有効バージョンの active 全ノードのフルパス（'／' 連結）を
 * 重複排除・日本語ソートして返す（Excel テンプレートのドロップダウン用）。
 *
 * options.affiliate が true（系列会社ありグループ）で組織表に系列会社（level 0）ノードがある場合は、
 * 各経路の先頭に系列会社名を付けた「会社名／所属①／…」を返し、系列会社名だけの行
 * （会社直下＝所属なし）も含める。系列会社の下に無いノードは含めない
 * （アップロード時に先頭セグメントを会社名として解釈するため）。
 * affiliate でない場合・level 0 ノードが無い場合は従来どおり。
 * @param {Array} units
 * @param {{ affiliate?: boolean }} [options]
 * @returns {string[]}
 */
export const buildOrgListPaths = (units, options = {}) => {
  // C′ 設計（2026-07）でノードの識別子は lineage_id（非表示 ObjectId 文字列）に統一。
  // 経路組立は lineage_id / parent_lineage_id を文字列比較で辿る。
  const byId = new Map();
  (units || []).forEach((u) => byId.set(String(u.lineage_id), u));
  // ノードの祖先チェーン（ルート → 当該ノード）
  const chainOf = (u) => {
    const chain = [];
    let cur = u;
    const guard = new Set();
    while (cur && !guard.has(String(cur.lineage_id))) {
      guard.add(String(cur.lineage_id));
      chain.unshift(cur);
      cur = cur.parent_lineage_id
        ? byId.get(String(cur.parent_lineage_id))
        : null;
    }
    return chain;
  };
  const affiliate =
    options.affiliate === true && (units || []).some((u) => u && u.level === 0);
  const paths = (units || [])
    .filter((u) => u.status === 'active')
    .map((u) => {
      const chain = chainOf(u);
      // 系列会社ありグループ: 先頭が系列会社（level 0）でない経路は含めない
      if (affiliate && !(chain.length > 0 && chain[0].level === 0)) return '';
      return chain.map((n) => n.name).join(PATH_SEP);
    })
    .filter((p) => p && p.trim() !== '');
  return Array.from(new Set(paths)).sort((a, b) => a.localeCompare(b, 'ja'));
};

/**
 * 性別セル値の正規化（テンプレートは 男／女 で記入 → DB は 1／2 で保存）。
 * - '男' → '1' / '女' → '2'
 * - 数値文字列（'1'/'2' 等）はそのまま（後方互換）
 * - それ以外（空欄・想定外の表記）は '' を返し、numOrUndef 経由で undefined 化
 *   （Number キャスト失敗による登録エラーを防ぎ、既存値保持／未設定にする）
 */
export const normalizeSexValue = (v) => {
  const s = v === null || v === undefined ? '' : String(v).trim();
  if (s === '男') return '1';
  if (s === '女') return '2';
  if (/^\d+$/.test(s)) return s;
  return '';
};

// CSV の所属列を「入力された所属」表示用に '／' 連結で組み立て
export const joinInputBelong = (record) => {
  const dept = expandDepartments(record);
  return DEPT_KEYS.map((k) => dept[k])
    .filter((v) => v != null && String(v).trim() !== '')
    .join(PATH_SEP);
};

// CSV 値の 1 セルをエスケープ（カンマ・改行・引用符を含む場合は "" 括り）
const csvEscape = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * 検証エラー行の CSV（BOM 付き UTF-8）を生成。
 * 列 = 行番号,氏名,入力された所属,エラー理由,候補
 * @param {Array} errorResults validate-roster の ok:false 結果配列
 * @param {Array} fileJSON     元 CSV データ（row-1 でレコードを引く）
 */
export const buildRosterErrorCsv = (errorResults, fileJSON) => {
  const header = ['行番号', '氏名', '入力された所属', 'エラー理由', '候補'];
  const lines = [header.join(',')];
  (errorResults || []).forEach((r) => {
    const rec = (fileJSON && fileJSON[r.row - 1]) || {};
    const name = `${rec.lastname || ''} ${rec.name || ''}`.trim();
    const belong = joinInputBelong(rec);
    const suggestions = (r.suggestions || [])
      .map((s) => s.path)
      .filter((p) => p)
      .join(' / ');
    lines.push(
      [r.row, name, belong, r.error || '', suggestions].map(csvEscape).join(',')
    );
  });
  // 先頭 BOM で Excel の文字化けを防止
  return '﻿' + lines.join('\r\n') + '\r\n';
};

// Blob を生成してダウンロードを発火（CSV/Excel 共通）
export const triggerBlobDownload = (data, fileName, mimeType) => {
  const blob = new Blob([data], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// yyyymmdd（テンプレートファイル名用）
export const ymd8 = () => {
  const d = new Date();
  return (
    d.getFullYear() +
    ('0' + (d.getMonth() + 1)).slice(-2) +
    ('0' + d.getDate()).slice(-2)
  );
};

// 名簿テンプレート（Excel）の列定義。
// ※ 実運用の「アセスメント情報シート（回答者リスト）」の並びに合わせつつ、
//   所属は department〜department7 の個別 7 列を「所属フルパス」1 列に置き換える
//   （個別列形式も引き続き読み込み可）。No. 列は様式再現用のシステム未使用列
//   （アップロード時は英字フィールド名が無いため無害に無視される）。
// - key: 4 行目（英字フィールド名 = 実質ヘッダー）に出力する値
// - jp:  3 行目（日本語ラベル）に出力する値
// - width: 列幅（概略）
// - required: 必須列（新規登録時。日本語ラベルを赤字にする）
const ROSTER_COLUMN_DEFS = [
  { key: 'No.', jp: 'No.', width: 9 }, // 様式再現用（システム未使用）
  { key: 'company', jp: '会社名', width: 23 },
  { key: 'lastname', jp: '氏名（姓）', width: 13, required: true },
  { key: 'name', jp: '氏名（名）', width: 13, required: true },
  { key: 'lastname_yomi', jp: 'ふりがな（姓）', width: 16 },
  { key: 'name_yomi', jp: 'ふりがな（名）', width: 16 },
  { key: 'email', jp: 'メールアドレス（＝ログインID）', width: 32, required: true },
  { key: 'password', jp: '初期パスワード', width: 18, required: true },
  { key: FULL_PATH_HEADER, jp: '所属（フルパス・選択式）', width: 24 },
  // 性別は 男／女 で記入（アップロード時に 男→1・女→2 へ変換して保存）
  { key: 'sex', jp: '性別（男／女）', width: 16 },
  { key: 'birthday', jp: '生年月日', width: 16 },
  { key: 'join_company', jp: '入社年月日', width: 16 },
  { key: 'emp_type', jp: '雇用形態', width: 16 },
  { key: 'go_out', jp: '出向状態', width: 16 },
  { key: 'position', jp: '職位（役職）', width: 16 },
  { key: 'level_code', jp: '等級', width: 16 },
  { key: 'work_time', jp: '勤務時間', width: 16 },
  { key: 'work_location', jp: '勤務地', width: 16 },
  // ── 実運用様式に無いシステム項目（既存の日本語名称を使用） ──
  // ※ 管理者フラグ(manager)・パスワード変更フラグ(change_password)・年齢層コード(age_range)・
  //   無効フラグ(inactive) は実運用で使用しないためテンプレート列から除外（Ph.3e ユーザー指示）
  { key: 'emp_no', jp: '社員番号', width: 16 },
  { key: 'work_time_rule', jp: '勤務時間制度・職制', width: 20 },
  { key: 'join_type', jp: '入社形態', width: 16 },
  { key: 'role', jp: 'role', width: 10 },
];

// 名簿テンプレートのヘッダー構成（英字フィールド名 = 4 行目に出力される実質ヘッダー）。
// ※ 後方互換のため export を維持（現行は本ファイル内でのみ使用）。
export const ROSTER_TEMPLATE_HEADERS = ROSTER_COLUMN_DEFS.map((c) => c.key);

// ── Excel セル配色（tokens.css と値を一致させる。Excel 内部色は
//    「コード内 HEX 禁止」の対象外＝バックエンド生成物と同扱い） ──
const FILL_HEADER = 'FFF2EBD5'; // --grey-10（ヘッダー行の背景）
const FONT_EXAMPLE = 'FF6B6760'; // --grey-65（記入例行の文字色）
const FONT_REQUIRED = 'FFB5302F'; // --red-active（必須列ラベルの文字色）

// 記入例 2 行の見本値（A 列 = No. は「例」を入れてデータと区別）。
// 所属フルパスは実データの経路を使い、無ければ '本社／管理部'。
// 系列会社ありグループ（affiliate）では所属フルパスが「会社名／…」形式のため、
// 所属を含む経路（'／' を含むもの）を優先して見本にし、会社名はその先頭セグメントに揃える。
const buildExampleRows = (paths, options = {}) => {
  const affiliate = options.affiliate === true;
  const companyNames = Array.isArray(options.companyNames) ? options.companyNames : [];
  let path1;
  let path2;
  let company1 = 'サンプル株式会社';
  let company2 = 'サンプル株式会社';
  if (affiliate) {
    const withDept = (paths || []).filter((p) => p.includes(PATH_SEP));
    const fallbackCompany = companyNames[0] || company1;
    path1 = withDept[0] || (paths && paths[0]) || `${fallbackCompany}${PATH_SEP}本社${PATH_SEP}管理部`;
    path2 = withDept[1] || path1;
    company1 = path1.split(PATH_SEP)[0];
    company2 = path2.split(PATH_SEP)[0];
  } else {
    path1 = (paths && paths[0]) || '本社／管理部';
    path2 = (paths && paths[1]) || path1;
  }
  // COLUMN_DEFS の key をキーにした見本オブジェクト（配列化は下で行う）
  const ex1 = {
    'No.': '例',
    company: company1,
    lastname: '山田',
    name: '太郎',
    lastname_yomi: 'やまだ',
    name_yomi: 'たろう',
    email: 'taro.yamada@example.com',
    password: 'Pass1234',
    [FULL_PATH_HEADER]: path1,
    sex: '男',
    birthday: '1990/01/15',
    join_company: '2015/04/01',
    emp_type: '正社員',
    go_out: '○○社出向中',
    position: '課長',
    level_code: 'M2',
    work_time: 'フレックス',
    work_location: '東京本社',
    emp_no: '10001',
    join_type: '新卒',
    role: '1',
  };
  const ex2 = {
    'No.': '例',
    company: company2,
    lastname: '田中',
    name: '花子',
    lastname_yomi: 'たなか',
    name_yomi: 'はなこ',
    email: 'hanako.tanaka@example.com',
    password: 'Pass5678',
    [FULL_PATH_HEADER]: path2,
    sex: '女',
    birthday: '1988/07/22',
    join_company: '2012/04/01',
    emp_type: '契約社員',
    go_out: '本社',
    position: '主任',
    level_code: 'S1',
    work_time: '通常',
    work_location: '大阪支社',
    emp_no: '10002',
    join_type: '中途',
    role: '1',
  };
  const toArray = (obj) =>
    ROSTER_COLUMN_DEFS.map((c) => (obj[c.key] !== undefined ? obj[c.key] : ''));
  return [toArray(ex1), toArray(ex2)];
};

/**
 * 名簿テンプレート（Excel Workbook）を生成して返す。
 * options.affiliate が true（系列会社ありグループ）のとき:
 *   - 「組織リスト」シート A 列 = 「会社名／所属①／…」形式の経路（系列会社名だけの行も含む）
 *   - 「組織リスト」シート B 列 = 系列会社名一覧（options.companyNames）
 *   - 「会社名」列に B 列を参照するドロップダウンを付与
 * それ以外は従来どおり（会社名列はドロップダウンなし）。
 * シート1「回答者リスト」= 実運用様式の 4 段構成:
 *   行1〜2 = 記入例（A 列「例」+ 見本値・グレー文字）
 *   行3    = 日本語ラベル（太字 + 薄い背景。必須列は赤字）
 *   行4    = 英字フィールド名（= 実質ヘッダー。太字 + 同背景）
 *   行5〜  = データ入力領域（所属フルパス列にドロップダウン）
 * 「所属フルパス」列にシート2「組織リスト」を参照するドロップダウン（データ検証）を付与。
 * @param {Array} units 有効バージョンの組織単位配列（active 全ノードからフルパスを抽出）
 * @returns {ExcelJS.Workbook}
 */
export const buildRosterTemplateWorkbook = (units, options = {}) => {
  const affiliate = options.affiliate === true;
  const companyNames = affiliate && Array.isArray(options.companyNames)
    ? options.companyNames.map((n) => trimText(n)).filter((n) => n !== '')
    : [];
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('回答者リスト');
  const listSheet = workbook.addWorksheet('組織リスト');

  // 組織リスト（フルパス）を A 列に並べる（系列会社ありグループは「会社名／…」形式）
  const paths = buildOrgListPaths(units, { affiliate });
  paths.forEach((p, i) => {
    listSheet.getCell(`A${i + 1}`).value = p;
  });
  const listCount = Math.max(paths.length, 1);
  // 系列会社ありグループ: 系列会社名一覧を B 列に並べる（会社名列のドロップダウン参照先）
  companyNames.forEach((name, i) => {
    listSheet.getCell(`B${i + 1}`).value = name;
  });
  const companyCount = Math.max(companyNames.length, 1);

  // 列幅
  ROSTER_COLUMN_DEFS.forEach((c, i) => {
    sheet.getColumn(i + 1).width = c.width;
  });

  // 行1〜2: 記入例（グレー文字でデータと区別）
  const exampleRows = buildExampleRows(paths, { affiliate, companyNames });
  exampleRows.forEach((values) => {
    const row = sheet.addRow(values);
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { color: { argb: FONT_EXAMPLE }, italic: true };
    });
  });

  // 行3: 日本語ラベル（太字 + 背景。必須列は赤字）
  const jpRow = sheet.addRow(ROSTER_COLUMN_DEFS.map((c) => c.jp));
  jpRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const def = ROSTER_COLUMN_DEFS[colNumber - 1];
    cell.font = {
      bold: true,
      color: def && def.required ? { argb: FONT_REQUIRED } : undefined,
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: FILL_HEADER },
    };
  });

  // 行4: 英字フィールド名（= 実質ヘッダー。太字 + 同背景）
  const keyRow = sheet.addRow(ROSTER_COLUMN_DEFS.map((c) => c.key));
  keyRow.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = { bold: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: FILL_HEADER },
    };
  });

  // 行5〜500: 「所属フルパス」列にデータ検証（リスト）を付与
  const pathColIndex =
    ROSTER_COLUMN_DEFS.findIndex((c) => c.key === FULL_PATH_HEADER) + 1;
  // 性別列にも 男／女 のドロップダウンを付与（誤記防止。アップロード時に 男→1・女→2 へ変換）
  const sexColIndex = ROSTER_COLUMN_DEFS.findIndex((c) => c.key === 'sex') + 1;
  // 系列会社ありグループのみ: 会社名列に「組織リスト」B 列（系列会社名）のドロップダウンを付与
  const companyColIndex = affiliate
    ? ROSTER_COLUMN_DEFS.findIndex((c) => c.key === 'company') + 1
    : 0;
  for (let r = 5; r <= 500; r++) {
    if (companyColIndex > 0) {
      sheet.getRow(r).getCell(companyColIndex).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`'組織リスト'!$B$1:$B$${companyCount}`],
      };
    }
    sheet.getRow(r).getCell(pathColIndex).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [`'組織リスト'!$A$1:$A$${listCount}`],
    };
    if (sexColIndex > 0) {
      sheet.getRow(r).getCell(sexColIndex).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ['"男,女"'],
      };
    }
  }

  return workbook;
};

/**
 * 名簿テンプレート（Excel）を生成してダウンロードを発火。
 * @param {Array} units 有効バージョンの組織単位配列
 * @param {string} groupCode ファイル名用のグループコード
 * @param {{ affiliate?: boolean, companyNames?: string[] }} [options] 系列会社ありグループ用（buildRosterTemplateWorkbook 参照）
 */
export const downloadRosterTemplate = async (units, groupCode, options = {}) => {
  const workbook = buildRosterTemplateWorkbook(units, options);
  const buffer = await workbook.xlsx.writeBuffer();
  triggerBlobDownload(
    buffer,
    `名簿テンプレート_${groupCode}_${ymd8()}.xlsx`,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
};

// ─── サンプル CSV（受講者 CSV 3 画面の「サンプル CSV ダウンロード」・2026-10-07） ───
// ヘッダーは英字フィールド名（アップロード時のキー）。所属は `department_path` 1 列（'／' 区切り。
// 系列会社ありグループは先頭が系列会社名）。更新のみ画面（CSVUserUpdateOnlyPage）はフルパス非対応のため個別列。
const SAMPLE_CSV_DEFS = {
  // 受講者 CSV アップロード（新規登録）
  create: {
    headers: ['email', 'password', 'lastname', 'name', 'company', FULL_PATH_HEADER, 'sex', 'birthday', 'position', 'level_code', 'emp_type', 'go_out', 'emp_no', 'role'],
    rows: (a) => [
      ['sample01@example.com', 'Pass1234', '山田', '太郎', a ? 'A社' : '', a ? 'A社／本社／営業部' : '本社／営業部', '男', '1985/04/01', '課長', 'M2', '正社員', '', 'E001', '1'],
      ['sample02@example.com', 'Pass5678', '佐藤', '花子', a ? 'B社' : '', a ? 'B社／工場／製造課' : '工場／製造課', '女', '1992/10/15', '一般', 'S1', '正社員', '', 'E002', '1'],
    ],
  },
  // メンバー情報更新（履歴あり）
  update: {
    headers: ['email', 'lastname', 'name', 'company', FULL_PATH_HEADER, 'position', 'level_code', 'emp_type', 'go_out', 'inactive', 'input_date'],
    rows: (a) => [
      ['sample01@example.com', '山田', '太郎', a ? 'A社' : '', a ? 'A社／本社／総務部' : '本社／総務部', '部長', 'M1', '正社員', '', 'false', '2026/04/01'],
      ['sample02@example.com', '佐藤', '花子', a ? 'B社' : '', a ? 'B社／工場／品質課' : '工場／品質課', '一般', 'S1', '正社員', '', 'false', '2026/04/01'],
    ],
  },
  // 受講者 CSV Update（NO GSD, NO VUD）: 個別列（フルパス非対応）
  updateOnly: {
    headers: ['email', 'lastname', 'name', 'company', 'department', 'department2', 'department3', 'department4', 'department5', 'position', 'level_code', 'emp_type', 'go_out', 'sex', 'emp_no'],
    rows: (a) => [
      ['sample01@example.com', '山田', '太郎', a ? 'A社' : '', '本社', '営業部', '', '', '', '課長', 'M2', '正社員', '', '1', 'E001'],
      ['sample02@example.com', '佐藤', '花子', a ? 'B社' : '', '工場', '製造課', '', '', '', '一般', 'S1', '正社員', '', '2', 'E002'],
    ],
  },
};
/**
 * サンプル CSV の文字列を作る（UTF-8 BOM 付き・CRLF）。
 * @param {'create'|'update'|'updateOnly'} kind 画面種別
 * @param {{ affiliate?: boolean }} [options] 系列会社ありグループなら会社名と「会社名／…」形式の例にする
 */
export const buildSampleRosterCsv = (kind, options = {}) => {
  const def = SAMPLE_CSV_DEFS[kind] || SAMPLE_CSV_DEFS.create;
  const a = options.affiliate === true;
  const lines = [def.headers, ...def.rows(a)].map((r) => r.map(csvEscape).join(','));
  return '\uFEFF' + lines.join('\r\n') + '\r\n';
};
export const SAMPLE_CSV_FILE_NAMES = {
  create: 'サンプル_受講者CSV_新規登録.csv',
  update: 'サンプル_受講者CSV_更新_履歴あり.csv',
  updateOnly: 'サンプル_受講者CSV_更新のみ.csv',
};
